import { UPDATE_CURRENT_LINEITEM, UPDATE_LAST_LINEITEM } from '../actionTypes';
import { isPuneJlr, OPERATIONS, REDIRECT_URLS, WORK_STREAM_TYPE } from '../../constants';
import { getNavigableOperations } from '../../utils/operationFlow';
import { axiosInstance } from '../../api/api';
import { updateOperations } from './operations';
import { updatePartValidation } from './partValidation';
import { updateTab } from './tab';
import { updateReceivePart } from './receivePart';
import { updateScanAttribute } from './scanAttribute';
import { updateModeType } from './modeType';

export const updateCurrentLineItem =
	(currentLineItem) => (dispatch, getState) => {
		const { user } = getState();
		const { station } = user;

		const state = { ...currentLineItem };
		const stationKey = (station || 'MAJOR_REPAIR').replaceAll(/[- ]/g, '_');
		let lineAndStationData = JSON.parse(
			localStorage.getItem(stationKey),
		);
		lineAndStationData = { ...lineAndStationData, currentLineItem: state };

		localStorage.setItem(
			stationKey,
			JSON.stringify(lineAndStationData),
		);

		dispatch({
			type: UPDATE_CURRENT_LINEITEM,
			currentLineItem: { ...state },
		});
	};

export const updateLastLineItem = (lastLineItem) => (dispatch, getState) => {
	if (!lastLineItem || Object.keys(lastLineItem).length === 0) {
		return;
	}

	const { user } = getState();
	const { station } = user;

	const storageKey = (station || 'MAJOR_REPAIR').replaceAll(/[- ]/g, '_');

	// Retrieve existing data from localStorage (or default to an empty array)
	let lineAndStationData = JSON.parse(localStorage.getItem(storageKey)) || {};

	// Ensure lastLineItem is stored as an array
	let lastLineItemsArray = Array.isArray(lineAndStationData.lastLineItem)
		? lineAndStationData.lastLineItem
		: [];

	// Add new lastLineItem at the beginning of the array
	if (
		lastLineItem &&
		typeof lastLineItem === 'object' &&
		!Array.isArray(lastLineItem) &&
		Object.keys(lastLineItem).length > 0
	) {
		lastLineItemsArray.unshift({ ...lastLineItem });
	}

	// Update storage object
	lineAndStationData.lastLineItem = lastLineItemsArray;

	// Save updated data back to localStorage
	localStorage.setItem(storageKey, JSON.stringify(lineAndStationData));

	// Dispatch action to update Redux state
	dispatch({
		type: UPDATE_LAST_LINEITEM,
		lastLineItem: [...lastLineItemsArray], // Ensures Redux state also maintains an array
	});
};

export const fetchLineUpcomingParts = (successCallback, errorCallback) => {
	return () => {
		axiosInstance
			.get('/line/upcoming-parts')
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const fetchStationUpcomingParts = (successCallback, errorCallback) => {
	return () => {
		axiosInstance
			.get('/station/upcoming-parts')
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const fetchStationTabUpcomingParts = (successCallback, errorCallback) => {
	return () => {
		axiosInstance
			.get('/station/get-upcoming-at-station')
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};
export const vinAvailability = (
	payload = null,
	successCallback,
	errorCallback,
) => {
	return (dispatch, getState) => {
		const { currentLineItem } = getState().station;
		axiosInstance
			.post(`/station/vin-availability`, payload)
			.then(({ data }) => {
				dispatch(
					updateCurrentLineItem({
						...currentLineItem,
					}),
				);
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const partTakeIn = (
	sequenceNumber,
	payload = null,
	successCallback,
	errorCallback,
) => {
	return (dispatch, getState) => {
		const { currentLineItem } = getState().station;
		axiosInstance
			.post(`/station/parts/${sequenceNumber}/take-in`, payload)
			.then(({ data }) => {
				dispatch(
					updateCurrentLineItem({
						...currentLineItem,
					}),
				);
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const emptyPitchItem = (successCallback, errorCallback) => {
	return axiosInstance
		.post(`/line/empty-pitch`)
		.then(({ data }) => {
			successCallback(data);
		})
		.catch((err) => {
			errorCallback(err);
		});
};

export const dropAsnOrAgsn = (
	sequenceNumber,
	successCallback,
	errorCallback,
) => {
	return (dispatch, getState) => {
		const { currentLineItem } = getState().station;

		axiosInstance
			.post(`/line/drop/${sequenceNumber}`)
			.then(({ data }) => {
				dispatch(
					updateCurrentLineItem({
						...currentLineItem,
					}),
				);
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const receiveAsnOrAgsn = (
	sequenceNumber,
	payload,
	successCallback,
	errorCallback,
) => {
	return (dispatch, getState) => {
		const { currentLineItem } = getState().station;

		axiosInstance
			.post(`/line/receive/${sequenceNumber}`, payload)
			.then(({ data }) => {
				dispatch(
					updateCurrentLineItem({
						...currentLineItem,
					}),
				);
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const fetchCurrentAndPreviousLineItem = (
	navigate,
	successCallback,
	currentPath = '',
) => {
	return async (dispatch, getState) => {
		const {
			user: { station, workStreamType },
			operations: { allowedOperations },
		} = getState();

		const stationStorageKey = (station || 'MAJOR_REPAIR').replaceAll(/[- ]/g, '_');

		let currentLineItem =
			JSON.parse(localStorage.getItem(stationStorageKey))
				?.currentLineItem || {};

		let lastLineItem =
			JSON.parse(localStorage.getItem(stationStorageKey))
				?.lastLineItem || {};

		let receivePart =
			JSON.parse(localStorage.getItem(stationStorageKey))
				?.receivePart || {};

		let modeType = localStorage.getItem('modeType');

		let partValidation =
			JSON.parse(localStorage.getItem(stationStorageKey))
				?.partValidation || {};

		let attribute =
			JSON.parse(localStorage.getItem(stationStorageKey))
				?.attribute || {};

		const navigationForScan = async () => {
			if (!receivePart || !receivePart?.serialNumber) {
				const receivePartPath = allowedOperations.includes(OPERATIONS.TRANSFER_DROP)
					? '/transfer-drop'
					: allowedOperations.includes(OPERATIONS.MAJOR_REPAIR)
					? '/assembly/major-repair/receive-part'
					: REDIRECT_URLS[OPERATIONS.RECEIVE_PART];
				navigate(receivePartPath);
				dispatch(
					updateOperations({
						currentOperation: allowedOperations.includes(OPERATIONS.TRANSFER_DROP)
							? OPERATIONS.TRANSFER_DROP
							: OPERATIONS.RECEIVE_PART,
					}),
				);
				return;
			}

			dispatch(updatePartValidation(partValidation));

			if (allowedOperations.includes(OPERATIONS.BOOK)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.BOOK,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.BOOK]);
			} else if (allowedOperations.includes(OPERATIONS.PROCESS_VALIDATION)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.PROCESS_VALIDATION,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.PROCESS_VALIDATION]);
			} else if (allowedOperations.includes(OPERATIONS.WTCL)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.WTCL,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.WTCL]);
			} else if (allowedOperations.includes(OPERATIONS.SFLT)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.SFLT,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.SFLT]);
			} else if (allowedOperations.includes(OPERATIONS.BODY_ALIGN)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.BODY_ALIGN,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.BODY_ALIGN]);
			} else if (allowedOperations.includes(OPERATIONS.WAHA)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.WAHA,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.WAHA]);
			} else if (allowedOperations.includes(OPERATIONS.RBT)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.RBT,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.RBT]);
			} else if (allowedOperations.includes(OPERATIONS.PAINT_SELECT)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.PAINT_SELECT,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.PAINT_SELECT]);
			} else if (allowedOperations.includes(OPERATIONS.PRE_CAL)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.PRE_CAL,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.PRE_CAL]);
			} else if (allowedOperations.includes(OPERATIONS.DT)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.DT,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.DT]);
			} else if (allowedOperations.includes(OPERATIONS.SHOWER)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.SHOWER,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.SHOWER]);
			} else if (allowedOperations.includes(OPERATIONS.MAJOR_REPAIR)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.MAJOR_REPAIR,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.MAJOR_REPAIR]);
			} else if (allowedOperations.includes(OPERATIONS.MACHINE)) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.MACHINE,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.MACHINE]);
			} else {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.PART_VALIDATION,
					}),
				);
				navigate(REDIRECT_URLS[OPERATIONS.PART_VALIDATION]);
			}
		};

		const navigateToNextScreen = () => {
			// WCPA stations are lot-based — never redirect away from /assembly/wcpa on load/refresh
			if (allowedOperations.includes(OPERATIONS.WCPA) && !allowedOperations.includes(OPERATIONS.RECEIVE_SN)) {
				dispatch(updateOperations({ currentOperation: OPERATIONS.WCPA }));
				if (currentPath !== REDIRECT_URLS[OPERATIONS.WCPA]) {
					navigate(REDIRECT_URLS[OPERATIONS.WCPA]);
				}
				return;
			}

			// SFLT stations are vehicle-list-based — never redirect away from /assembly/sflt on load/refresh
			if (allowedOperations.includes(OPERATIONS.SFLT) && !allowedOperations.includes(OPERATIONS.RECEIVE_SN)) {
				dispatch(updateOperations({ currentOperation: OPERATIONS.SFLT }));
				if (currentPath !== REDIRECT_URLS[OPERATIONS.SFLT]) {
					navigate(REDIRECT_URLS[OPERATIONS.SFLT]);
				}
				return;
			}

			// MAJOR_REPAIR stations are vehicle-list-based — never redirect away from /assembly/major-repair/receive-part on load/refresh
			if (allowedOperations.includes(OPERATIONS.MAJOR_REPAIR) && !allowedOperations.includes(OPERATIONS.RECEIVE_SN)) {
				dispatch(updateOperations({ currentOperation: OPERATIONS.MAJOR_REPAIR }));
				if (
					currentPath !== '/assembly/major-repair/receive-part' &&
					currentPath !== '/assembly/station/major-repair' &&
					currentPath !== '/assembly/Major-repair/receive-part'
				) {
					navigate('/assembly/major-repair/receive-part');
				}
				return;
			}

			// TRANSFER_DROP / WAHA stations manage their own entry flow via /transfer-drop when no active part
			if (allowedOperations.includes(OPERATIONS.TRANSFER_DROP) && !currentLineItem?.serialNumber && !allowedOperations.includes(OPERATIONS.RECEIVE_SN)) {
				dispatch(updateOperations({ currentOperation: OPERATIONS.TRANSFER_DROP }));
				if (currentPath !== REDIRECT_URLS[OPERATIONS.TRANSFER_DROP]) {
					navigate(REDIRECT_URLS[OPERATIONS.TRANSFER_DROP]);
				}
				return;
			}

			let nextOperation;
			const navigableOperations = getNavigableOperations(allowedOperations);

			// If no current line item or no completed operations, start with first operation
			if (
				!Object.keys(currentLineItem).length ||
				!currentLineItem?.completedOperations?.length
			) {
				// If WTCL or similar special flow operations are present
				if (
					allowedOperations.includes(OPERATIONS.WTCL) ||
					allowedOperations.includes(OPERATIONS.SFLT) ||
					allowedOperations.includes(OPERATIONS.BODY_ALIGN) ||
					allowedOperations.includes(OPERATIONS.RBT) ||
					allowedOperations.includes(OPERATIONS.PAINT_SELECT) ||
					allowedOperations.includes(OPERATIONS.PRE_CAL) ||
					allowedOperations.includes(OPERATIONS.DT) ||
					allowedOperations.includes(OPERATIONS.SHOWER) ||
					allowedOperations.includes(OPERATIONS.MAJOR_REPAIR)
				) {
					// If RECEIVE_SN is present, we should start with standard Scan VIN flow
					if (allowedOperations.includes(OPERATIONS.RECEIVE_SN)) {
						nextOperation = OPERATIONS.RECEIVE_SN;
					}
					// Otherwise, use RECEIVE_PART as the entry point
					else {
						nextOperation = allowedOperations.includes(OPERATIONS.RECEIVE_PART)
							? OPERATIONS.RECEIVE_PART
							: (allowedOperations.find((op) => op !== OPERATIONS.TAKE_IN_OUT) || allowedOperations[0]);
					}
				} else {
					nextOperation = navigableOperations.includes(OPERATIONS.RECEIVE_SN)
						? OPERATIONS.RECEIVE_SN
						: (navigableOperations[0] || allowedOperations[0]);
				}
			}
			// If all required operations are completed:
			// - go to scan-vin only when RECEIVE_SN exists
			// - otherwise go to receive-part
			else if (
				navigableOperations
					.every((op) => currentLineItem.completedOperations.includes(op))
			) {
				// For ENGRAVE stations: if a current part still exists (has serialNumber),
				// stay on the engraving screen instead of bouncing to scan-vin.
				if (
					allowedOperations.includes(OPERATIONS.ENGRAVING) &&
					currentLineItem?.serialNumber
				) {
					nextOperation = OPERATIONS.ENGRAVING;
				}
				// For WCPA-only stations, loop back to WCPA (lot-based, no per-part cycle)
				else if (
					allowedOperations.includes(OPERATIONS.WCPA) &&
					!allowedOperations.includes(OPERATIONS.RECEIVE_SN)
				) {
					nextOperation = OPERATIONS.WCPA;
				}
				// If WTCL or SFLT is present, go back to receive-part instead of scan-vin
				else if (
					allowedOperations.includes(OPERATIONS.WTCL) ||
						allowedOperations.includes(OPERATIONS.SFLT) ||
						allowedOperations.includes(OPERATIONS.BODY_ALIGN) ||
						allowedOperations.includes(OPERATIONS.RBT) ||
						allowedOperations.includes(OPERATIONS.PAINT_SELECT) ||
						allowedOperations.includes(OPERATIONS.PRE_CAL) ||
						allowedOperations.includes(OPERATIONS.DT) ||
						allowedOperations.includes(OPERATIONS.SHOWER) ||
						allowedOperations.includes(OPERATIONS.MAJOR_REPAIR)
				) {
					nextOperation = OPERATIONS.RECEIVE_PART;
				} else if (allowedOperations.includes(OPERATIONS.TRANSFER_DROP)) {
					nextOperation = OPERATIONS.TRANSFER_DROP;
				} else {
					nextOperation = navigableOperations.includes(OPERATIONS.RECEIVE_SN)
						? OPERATIONS.RECEIVE_SN
						: OPERATIONS.RECEIVE_PART;
				}
			}
			// Find the next incomplete operation
			else if (currentLineItem.completedOperations.length) {
    nextOperation = navigableOperations.find((op) => {
        if (
            (allowedOperations.includes(OPERATIONS.WTCL) ||
                allowedOperations.includes(OPERATIONS.SFLT) ||
                allowedOperations.includes(OPERATIONS.BODY_ALIGN) ||
                allowedOperations.includes(OPERATIONS.WAHA) ||
                allowedOperations.includes(OPERATIONS.RBT) ||
                allowedOperations.includes(OPERATIONS.PAINT_SELECT) ||
                allowedOperations.includes(OPERATIONS.PRE_CAL) ||
                allowedOperations.includes(OPERATIONS.DT) ||
                allowedOperations.includes(OPERATIONS.SHOWER) ||
                allowedOperations.includes(OPERATIONS.MAJOR_REPAIR)) &&
            op === OPERATIONS.RECEIVE_SN
        ) {
            return false;
        }

       
        if (
            allowedOperations.includes(OPERATIONS.TRANSFER_DROP) &&
            op === OPERATIONS.RECEIVE_SN
        ) {
            return false;
        }

        return !currentLineItem.completedOperations.includes(op);
    });
}

			const hasCompletedPartValidation =
				currentLineItem?.completedOperations?.includes(
					OPERATIONS.PART_VALIDATION,
				);
			const hasPendingMachineOperation =
				allowedOperations.includes(OPERATIONS.MACHINE) &&
				!currentLineItem?.completedOperations?.includes(OPERATIONS.MACHINE);
			const hasActiveReceivePartInFlow = !!receivePart?.serialNumber;
			const isOnPartValidationPath =
				currentPath === REDIRECT_URLS[OPERATIONS.PART_VALIDATION];

			// Keep user on Part Validation completion screen so they can review
			// the preview and explicitly click "Next Operation" to enter MACHINE.
			if (
				(hasActiveReceivePartInFlow || isOnPartValidationPath) &&
				hasCompletedPartValidation &&
				hasPendingMachineOperation &&
				currentPath !== REDIRECT_URLS[OPERATIONS.MACHINE]
			) {
				dispatch(
					updateOperations({
						currentOperation: OPERATIONS.PART_VALIDATION,
					}),
				);
				if (currentPath !== REDIRECT_URLS[OPERATIONS.PART_VALIDATION]) {
					navigate(REDIRECT_URLS[OPERATIONS.PART_VALIDATION]);
				}
				return;
			}

			// Determine the expected path for the next operation
			let expectedPath;
			switch (nextOperation) {
				case OPERATIONS.DROP:
					expectedPath =
						workStreamType === WORK_STREAM_TYPE.POOL ? '/scan-vin' : '/';
					break;
				case OPERATIONS.RECEIVE_SN:
					expectedPath = REDIRECT_URLS[OPERATIONS.RECEIVE_SN];
					break;
				case OPERATIONS.TRANSFER_DROP:
					expectedPath = REDIRECT_URLS[OPERATIONS.TRANSFER_DROP];
					break;
				case OPERATIONS.RECEIVE_PART:
				case OPERATIONS.PROCESS_VALIDATION:
				case OPERATIONS.ENGRAVING:
				case OPERATIONS.MACHINE:
				case OPERATIONS.TAKE_IN_OUT:
				case OPERATIONS.WCPA:
					expectedPath = REDIRECT_URLS[nextOperation];
					break;
				case OPERATIONS.WTCL:
				case OPERATIONS.SFLT:
				case OPERATIONS.BODY_ALIGN:
				case OPERATIONS.WAHA:
				case OPERATIONS.RBT:
				case OPERATIONS.PAINT_SELECT:
				case OPERATIONS.PRE_CAL:
				case OPERATIONS.DT:
				case OPERATIONS.SHOWER:
				case OPERATIONS.MAJOR_REPAIR:
				case OPERATIONS.PART_VALIDATION:
				case OPERATIONS.BOOK:
					// TRANSFER_DROP flow: navigate directly to WAHA without RECEIVE_PART
					if (allowedOperations.includes(OPERATIONS.TRANSFER_DROP)) {
						expectedPath = REDIRECT_URLS[nextOperation];
					} else {
						// These operations need special handling via navigationForScan
						expectedPath = null;
					}
					break;
				default:
					expectedPath = '/scan-vin';
					break;
			}

			// Only navigate if we're not already on the expected path
			if (expectedPath && currentPath !== expectedPath) {
				// Handle navigation based on operation type
				switch (nextOperation) {
					case OPERATIONS.DROP:
						dispatch(updateOperations({ currentOperation: OPERATIONS.DROP }));
						navigate(expectedPath);
						break;
					case OPERATIONS.RECEIVE_SN:
						navigate(expectedPath);
						dispatch(
							updateOperations({
								currentOperation: OPERATIONS.RECEIVE_SN,
							}),
						);
						break;
					case OPERATIONS.TRANSFER_DROP:
						navigate(expectedPath);
						dispatch(
							updateOperations({
								currentOperation: OPERATIONS.TRANSFER_DROP,
							}),
						);
						break;
					case OPERATIONS.RECEIVE_PART:
					case OPERATIONS.PROCESS_VALIDATION:
					case OPERATIONS.ENGRAVING:
					case OPERATIONS.MACHINE:
					case OPERATIONS.TAKE_IN_OUT:
					case OPERATIONS.WCPA:
						navigate(expectedPath);
						dispatch(
							updateOperations({
								currentOperation: nextOperation,
							}),
						);
						break;
					case OPERATIONS.WTCL:
					case OPERATIONS.SFLT:
					case OPERATIONS.BODY_ALIGN:
					case OPERATIONS.WAHA:
					case OPERATIONS.RBT:
					case OPERATIONS.PAINT_SELECT:
					case OPERATIONS.PRE_CAL:
					case OPERATIONS.DT:
					case OPERATIONS.SHOWER:
					case OPERATIONS.MAJOR_REPAIR:
					case OPERATIONS.PART_VALIDATION:
					case OPERATIONS.BOOK:
						if (allowedOperations.includes(OPERATIONS.TRANSFER_DROP)) {
							navigate(expectedPath);
							dispatch(updateOperations({ currentOperation: nextOperation }));
						} else {
							navigationForScan();
						}
						break;
					default:
						navigate(expectedPath);
						dispatch(
							updateOperations({
								currentOperation: OPERATIONS.RECEIVE_SN,
							}),
						);
						break;
				}
			} else {
				// Update operations state even if we don't navigate
				dispatch(updateOperations({ currentOperation: nextOperation }));
			}
		};

		try {
			const isReceiveSnEntryPath =
				currentPath === REDIRECT_URLS[OPERATIONS.RECEIVE_SN];

			// For Scan VIN entry flow, do not hydrate from current-part API.
			// This prevents stale/incomplete backend state from forcing a wrong screen.
			if (!Object.keys(currentLineItem).length && !isReceiveSnEntryPath) {
    const isMrStation = allowedOperations.includes(OPERATIONS.MAJOR_REPAIR);
    const { data } = await axiosInstance.get(isMrStation ? '/mr/station/current-part-on-mr' : '/station/current-part');
    currentLineItem = data || {};
    if (isMrStation && data) {
        currentLineItem.serialNumber = data.serialNo || data.serialNumber;
        currentLineItem.sequenceNumber = data.asn || data.sequenceNumber;
    }

    if (
        allowedOperations.includes(OPERATIONS.TRANSFER_DROP) &&
        currentLineItem?.serialNumber &&
        !currentLineItem?.completedOperations?.includes(OPERATIONS.TRANSFER_DROP)
    ) {
        currentLineItem = {
            ...currentLineItem,
            completedOperations: [
                OPERATIONS.TRANSFER_DROP,
                ...(currentLineItem.completedOperations || []),
            ],
        };
    }
}
		} catch (err) { }

		dispatch(updateCurrentLineItem(currentLineItem));
		dispatch(updateLastLineItem(lastLineItem));
		dispatch(updateReceivePart(receivePart));
		dispatch(updateScanAttribute(attribute));

		navigateToNextScreen();

		dispatch(updateTab({ tabValue: 0 }));

		dispatch(updateModeType(modeType));

		if (successCallback) successCallback();
	};
};

export const fetchProcessedAgsns = (successCallback, errorCallback) => {
	return async () => {
		try {
			const response = await axiosInstance.get('/station/parts?type=TAKEN_OUT');
			const { data } = response;
			successCallback(data);
		} catch (error) {
			errorCallback(error);
		}
	};
};
export const fetchSerialNumberDetails = (
	serialNumber,
	successCallback,
	errorCallback,
) => {
	return () => {
		axiosInstance
			.get(`/station/serial-number-details/${serialNumber}`)
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const fetchProcessedData = (stationName, successCallback, errorCallback) => {
	// Call custom station history APIs based on any assembly station name
	//hard coding date range temporarily!!
	if (stationName === 'WCPA' && isPuneJlr()) {
		return () => {
			axiosInstance
				.post('/wcpa/history', {
					from: null,
					to: null,
				})
				.then(({ data }) => {
					successCallback(data);
				})
				.catch((err) => {
					errorCallback(err);
				});
		};
	} else {
		return () => {
			axiosInstance
				.get('/station-history')
				.then(({ data }) => {
					successCallback(data);
				})
				.catch((err) => {
					errorCallback(err);
				});
		};
	}
};

export const fetchAvailableTransferBJCs = (successCallback, errorCallback) => {
	return () => {
		axiosInstance
			.get('/transfer-line/available-transfer-bjcs')
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const checkTransferLocation = (
	serialNumber,
	successCallback,
	errorCallback,
) => {
	return () => {
		axiosInstance
			.get(`/transfer-line/check-location/${serialNumber}`)
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};

export const dropTransferLine = (asn, successCallback, errorCallback) => {
	return () => {
		axiosInstance
			.post(`/transfer-line/drop/${asn}`)
			.then(({ data }) => {
				successCallback(data);
			})
			.catch((err) => {
				errorCallback(err);
			});
	};
};
